import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { Box, useTheme } from '@mui/material';
import { Contact } from '../contacts/ContactModal';
import { Initiative } from '../../types/database';
import { useNavigate } from 'react-router-dom';

interface EntityValenceProps {
  contacts: Contact[];
  initiatives: Initiative[];
}

interface Entity {
  id: string;
  name: string;
  type: 'contact' | 'initiative';
  totalConnections: number;
  y: number;
}

interface Connection {
  source: Entity;
  target: Entity;
  type: 'initiative' | 'association';
}

const LABEL_MAX_LENGTH = 22;

export function EntityValence({ contacts, initiatives }: EntityValenceProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (!svgRef.current || !contacts.length || !initiatives.length) return;

    // Clear previous visualization
    d3.select(svgRef.current).selectAll('*').remove();

    // Set up dimensions and margins
    const margin = { top: 20, right: 20, bottom: 20, left: 200 };
    const width = 1600;
    const height = Math.max(600, (contacts.length + initiatives.length) * 30);
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Calculate total connections for each entity
    const entities: Entity[] = [
      ...contacts.map(contact => ({
        id: contact.id,
        name: contact.name,
        type: 'contact' as const,
        totalConnections: contact.associated_contacts.length + 
          initiatives.filter(i => i.contacts.includes(contact.id)).length,
        y: 0 // Will be set later
      })),
      ...initiatives.map(initiative => ({
        id: initiative.id,
        name: initiative.name,
        type: 'initiative' as const,
        totalConnections: initiative.contacts.length + 
          initiative.related_initiatives.length,
        y: 0 // Will be set later
      }))
    ].sort((a, b) => a.totalConnections - b.totalConnections);

    // Assign y positions
    entities.forEach((entity, i) => {
      entity.y = (i * innerHeight) / (entities.length - 1);
    });

    // Create connections
    const connections: Connection[] = [];

    // Add initiative-contact connections
    initiatives.forEach(initiative => {
      initiative.contacts.forEach(contactId => {
        const contact = entities.find(e => e.id === contactId);
        const initiativeEntity = entities.find(e => e.id === initiative.id);
        if (contact && initiativeEntity) {
          connections.push({
            source: contact,
            target: initiativeEntity,
            type: 'initiative'
          });
        }
      });
    });

    // Add contact-contact connections
    contacts.forEach(contact => {
      contact.associated_contacts.forEach(associatedId => {
        const source = entities.find(e => e.id === contact.id);
        const target = entities.find(e => e.id === associatedId);
        if (source && target) {
          connections.push({
            source,
            target,
            type: 'association'
          });
        }
      });
    });

    // Add initiative-initiative connections
    initiatives.forEach(initiative => {
      initiative.related_initiatives.forEach(relatedId => {
        const source = entities.find(e => e.id === initiative.id);
        const target = entities.find(e => e.id === relatedId);
        if (source && target) {
          connections.push({
            source,
            target,
            type: 'association'
          });
        }
      });
    });

    // Create SVG
    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Draw connections as Bezier curves that bow out to the right
    const curveOffset = innerWidth * 0.95; // use 90% of the available width
    connections.forEach(connection => {
      const x = 0;
      const y1 = connection.source.y;
      const y2 = connection.target.y;
      const c1x = x + curveOffset;
      const c2x = x + curveOffset;
      const path = `M${x},${y1} C${c1x},${y1} ${c2x},${y2} ${x},${y2}`;
      svg.append('path')
        .attr('d', path)
        .attr('fill', 'none')
        .attr('stroke', connection.type === 'initiative' ? theme.palette.success.main : theme.palette.error.main)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', connection.type === 'initiative' ? 'none' : '4,4')
        .attr('opacity', 0.3);
    });

    // Draw entity dots and labels (single column)
    entities.forEach(entity => {
      // Create a group for each entity
      const entityGroup = svg.append('g')
        .style('cursor', 'pointer')
        .on('click', () => {
          navigate(`/${entity.type}s/${entity.id}`);
        });

      // Draw dot
      entityGroup.append('circle')
        .attr('cx', 0)
        .attr('cy', entity.y)
        .attr('r', 4)
        .attr('fill', entity.type === 'contact' ? theme.palette.primary.main : theme.palette.secondary.main);

      // Truncate label if too long
      const label = `${entity.name} (${entity.totalConnections})`;
      const truncated = label.length > LABEL_MAX_LENGTH ? label.slice(0, LABEL_MAX_LENGTH - 3) + '...' : label;

      // Draw label
      entityGroup.append('text')
        .attr('x', -10)
        .attr('y', entity.y)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .attr('fill', theme.palette.text.primary)
        .attr('font-size', 13)
        .text(truncated)
        .append('title')
        .text(label);
    });

  }, [contacts, initiatives, theme, navigate]);

  return (
    <Box sx={{ width: '100%', overflow: 'auto' }}>
      <svg ref={svgRef} style={{ display: 'block', margin: 'auto' }} />
    </Box>
  );
} 